import { getDownloadURL, getStorage, ref, uploadBytesResumable, type UploadTask } from 'firebase/storage';

const previewUrlCache = new Map<string, Promise<string>>();

export const uploadFile = (
  userId: string,
  file: File,
  onProgress?: (progress: number) => void,
  onComplete?: () => void,
  onError?: (error: Error) => void,
  storageFileName?: string
): UploadTask => {
  const storage = getStorage();
  const storagePath = `users/${userId}/${storageFileName ?? file.name}`;
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, file);

  if (onProgress) {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // progress handler
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress(progress);
      },
      (error) => {
        // Error handler
        console.error(error);
        onError?.(error);
      },
      onComplete
    );
  }

  return uploadTask;
};

// Storage objects are never rewritten (uploads are id-prefixed and a generated preview is written
// once), so a resolved download URL stays valid for the session.
export const previewFile = (storagePath: string): Promise<string> => {
  const cachedUrl = previewUrlCache.get(storagePath);
  if (cachedUrl) return cachedUrl;

  const storage = getStorage();
  const fileRef = ref(storage, storagePath);
  const previewUrl = getDownloadURL(fileRef);
  previewUrlCache.set(storagePath, previewUrl);

  // A missing object stays cached: the source list probes the preview path of every image that
  // predates previews on each card mount, and that 404 is the answer for the whole session. Any
  // other failure is forgotten so the next request retries.
  void previewUrl.catch((error) => {
    if (isFileNotFoundError(error) || previewUrlCache.get(storagePath) !== previewUrl) return;
    previewUrlCache.delete(storagePath);
  });

  return previewUrl;
};

// The one way a cached lookup goes stale within a session: an object that was missing appears
// later. Reprocessing a source can generate its preview, so the card forgets the preview path
// when the source goes back into processing and probes it afresh once ready.
export const forgetPreviewUrl = (storagePath: string): void => {
  previewUrlCache.delete(storagePath);
};

// Download URLs are scoped to the signed-in user's storage prefix, so they must not outlive the
// session. Called from clearRxDB alongside the other module-level caches.
export const clearPreviewUrlCache = (): void => {
  previewUrlCache.clear();
};

export const isFileNotFoundError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'FirebaseError' && (error as { code?: unknown }).code === 'storage/object-not-found';
